// src/pages/ia-paca/metiers/data-scientist.tsx
//
// Page métier — Data Scientist en PACA.
//
// Stratégie SEO : capter "recrutement Data Scientist", "data scientist
// Marseille", "data scientist Sophia", "salaire data scientist 2026",
// "différence data scientist / ML engineer / AI engineer".

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../../components/layout/layout';
import IaPacaHero from '../../../components/ia-paca/IaPacaHero';
import IaPacaCTA from '../../../components/ia-paca/IaPacaCTA';
import IaSalaryTable from '../../../components/ia-paca/IaSalaryTable';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  BarChart3,
  Database,
  FlaskConical,
  Lightbulb,
  Target,
  BookOpen,
} from 'lucide-react';

export default function DataScientistPage() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Occupation',
    name: 'Data Scientist',
    description:
      "Le Data Scientist transforme un problème business flou en question quantifiable, conçoit la méthodologie d'analyse, modélise, et restitue. Profil le plus généraliste de la famille IA, et le plus demandé en PACA.",
    occupationLocation: [
      { '@type': 'City', name: 'Marseille' },
      { '@type': 'City', name: 'Sophia Antipolis' },
      { '@type': 'City', name: 'Aix-en-Provence' },
      { '@type': 'City', name: 'Nice' },
      { '@type': 'AdministrativeArea', name: "Provence-Alpes-Côte d'Azur" },
    ],
    estimatedSalary: [
      {
        '@type': 'MonetaryAmountDistribution',
        currency: 'EUR',
        duration: 'P1Y',
        percentile10: 38000,
        median: 60000,
        percentile90: 90000,
      },
    ],
    skills:
      'Python, pandas, scikit-learn, statsmodels, SQL, R, Jupyter, dataviz (matplotlib, seaborn, Plotly), statistiques, A/B testing, causal inference, NLP, modèles de séries temporelles, communication',
    educationRequirements:
      "Master en statistiques, mathématiques appliquées, IA, économie quantitative, ou ingénieur grande école avec spécialisation data.",
    experienceRequirements: 'Junior (0-2 ans) à Senior (8+ ans).',
  };

  return (
    <Layout
      title="Recrutement Data Scientist en PACA — Marseille, Aix, Sophia | recruteurs.io"
      description="Recruter un Data Scientist en PACA : profils stats, ML, NLP, A/B testing. Cabinet de recrutement spécialisé qui sait distinguer un vrai DS d'un analyste Excel renommé. Salaire 38-90k€."
      keywords="recrutement Data Scientist, Data Scientist Marseille, Data Scientist Sophia Antipolis, salaire Data Scientist 2026, recruter Data Scientist PACA, Data Scientist Nice, embaucher Data Scientist"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'Data Scientist', href: '/ia-paca/metiers/data-scientist' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <IaPacaHero
        eyebrow="Métier IA · PACA"
        title="Recruter un"
        highlight="Data Scientist"
        subtitle="Le métier le plus demandé, le plus vaste, le plus mal défini. Un bon Data Scientist combine 3 choses qu'on trouve rarement ensemble : rigueur statistique, capacité à comprendre un problème business, et discipline pour produire des résultats actionnables. Le reste sont des consultants Excel."
        badges={['Stats', 'ML classique', 'A/B testing', 'NLP', 'Causal inference']}
      />

      <div className="bg-white pt-6">
        <div className="mx-auto max-w-4xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Tous les métiers IA
          </Link>
        </div>
      </div>

      {/* C'est quoi vraiment */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Le métier — sans bullshit
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Un Data Scientist en 2026, c'est quoi vraiment ?
          </h2>
          <div className="prose prose-slate mt-6 max-w-none text-base leading-relaxed text-slate-700">
            <p>
              Le Data Scientist transforme un{' '}
              <strong>problème business flou</strong> en{' '}
              <strong>question quantifiable</strong>, conçoit la méthodologie
              d'analyse adéquate, modélise (de la simple régression à un LLM
              fine-tuné selon le besoin), et — surtout — restitue de façon
              qu'une décision business puisse en sortir.
            </p>
            <p>
              Sur une journée type : il alterne entre SQL pour explorer la
              donnée, Python / R pour modéliser, échanges avec les équipes
              métier pour valider l'orientation, et restitution (notebook,
              dashboard, rapport). Un Data Scientist Senior anime aussi
              l'agenda data de son équipe : quel problème vaut le coup
              d'être modélisé, quel modèle déjà existant peut être réutilisé.
            </p>
            <p>
              <strong>Le piège du marché :</strong> le titre "Data Scientist"
              recouvre 4 réalités très différentes : (1) statisticien
              rigoureux, (2) ML practitioner qui shippe en prod, (3) analyste
              SQL/BI rebadgé, (4) chercheur académique en transition. Avant
              de recruter, il faut savoir lequel des quatre on cherche —
              c'est ce qu'on cadre en brief.
            </p>
          </div>
        </div>
      </section>

      {/* Les 4 typologies */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Les 4 typologies
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Quel Data Scientist vous voulez vraiment ?
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Avant de publier votre offre, identifiez clairement à laquelle
            de ces 4 catégories vous voulez accéder. Une fausse réponse, et
            vous perdez 2 mois à recruter le mauvais profil.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            <TypoCard
              Icon={BarChart3}
              tag="Type 1"
              title="Statisticien / Causal"
              body="Master stats, expérience inference causale, A/B testing, modèles d'attribution. Indispensable en marketing data, FinTech, MedTech, économétrie. Salaire confirmé : 55-80 k€."
            />
            <TypoCard
              Icon={FlaskConical}
              tag="Type 2"
              title="ML Practitioner production-ready"
              body="Sait shipper un modèle qui tourne. Stack scikit-learn / XGBoost / Spark ML + Docker + MLflow basique. Bon pour scale-ups et corporates qui veulent du ML qui dure. Salaire confirmé : 50-75 k€."
            />
            <TypoCard
              Icon={Database}
              tag="Type 3"
              title="Analyst SQL/BI rebadgé"
              body="Excellent en data prep, SQL, dashboards. Moins de modélisation, mais grande valeur business. Souvent confondu avec un vrai DS — à ce poste préfère Analytics Engineer ou Senior Data Analyst pour clarifier."
            />
            <TypoCard
              Icon={BookOpen}
              tag="Type 4"
              title="Chercheur académique en transition"
              body="Sortie PhD, fort en théorie, modèles avancés (deep learning, bayesien, time series). Atout : capacité à innover. Risque : sur-engineering, peu d'expérience production. À encadrer."
            />
          </div>
        </div>
      </section>

      {/* Stack technique */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Stack technique 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Les outils qu'un Data Scientist confirmé maîtrise.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            <StackCard
              Icon={Lightbulb}
              title="Langages & analyse"
              tools={['Python', 'SQL (avancé : window fns, CTE)', 'R (selon contexte)', 'Bash basique']}
            />
            <StackCard
              Icon={FlaskConical}
              title="Modélisation"
              tools={['scikit-learn', 'XGBoost / LightGBM', 'statsmodels', 'PyMC / Stan (bayesien)', 'prophet / NeuralProphet', 'Hugging Face (NLP)']}
            />
            <StackCard
              Icon={Target}
              title="Expérimentation & A/B"
              tools={['GrowthBook', 'Optimizely', 'Eppo', 'tests de puissance', 'CUPED']}
            />
            <StackCard
              Icon={BarChart3}
              title="Restitution & dataviz"
              tools={['matplotlib', 'seaborn', 'Plotly', 'Altair', 'Streamlit / Gradio', 'Tableau / Power BI / Metabase']}
            />
            <StackCard
              Icon={Database}
              title="Data warehouse / lakehouse"
              tools={['BigQuery', 'Snowflake', 'Databricks', 'dbt', 'Postgres + pg_vector', 'DuckDB']}
            />
            <StackCard
              Icon={Lightbulb}
              title="MLOps lite"
              tools={['MLflow', 'DVC', 'Docker basique', 'CI/CD GitHub Actions', 'monitoring : Evidently']}
            />
          </div>
        </div>
      </section>

      {/* Salaires Data Scientist */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Salaires Data Scientist en PACA — mars 2026
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            38-90 k€, fortes variations selon le type de poste.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Le marché Data Scientist en PACA est plus liquide que celui de
            l'AI Engineer ou du MLOps. Variations fortes selon le secteur
            (FinTech / santé +10-15%, public -10-15%) et la séniorité.
          </p>
          <div className="mt-8">
            <SalaireDsTable />
          </div>
        </div>
      </section>

      {/* Questions pour démasquer */}
      <section className="bg-white py-12 lg:py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Détecter les faux Data Scientists
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            5 questions pour qualifier un Data Scientist.
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Ces questions ne sont pas des "leetcode". Elles révèlent
            l'expérience réelle vs les certificats LinkedIn.
          </p>
          <div className="mt-8 space-y-4">
            {QUESTIONS.map((q, i) => (
              <div
                key={q.q}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-bold text-violet-700">
                    {i + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-slate-900">{q.q}</h3>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Bon signe
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-emerald-900">{q.good}</p>
                      </div>
                      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
                        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-rose-700">
                          <XCircle className="h-3.5 w-3.5" />
                          Drapeau rouge
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-rose-900">{q.bad}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tableau salaires global */}
      <section className="bg-slate-50 py-12 lg:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Comparer avec les autres métiers IA en PACA
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Tableau des salaires IA en PACA.
          </h2>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
        </div>
      </section>

      <IaPacaCTA
        title="Vous cherchez un Data Scientist en PACA ?"
        subtitle="Notre brief commence par identifier lequel des 4 types de Data Scientist vous voulez. Court-list 5-7 candidats qualifiés en 15-20 jours."
      />
    </Layout>
  );
}

function TypoCard({
  Icon,
  tag,
  title,
  body,
}: {
  Icon: typeof BarChart3;
  tag: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-violet-300 hover:shadow-md">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
        <Icon className="h-5 w-5" />
      </div>
      <span className="mt-3 inline-block rounded-full bg-pink-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-pink-700">
        {tag}
      </span>
      <h3 className="mt-2 text-base font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

function StackCard({
  Icon,
  title,
  tools,
}: {
  Icon: typeof Lightbulb;
  title: string;
  tools: string[];
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-violet-600" />
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {tools.map((t) => (
          <span
            key={t}
            className="inline-flex items-center rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

function SalaireDsTable() {
  const rows = [
    { ville: 'Marseille', junior: '38 – 48 k€', confirme: '48 – 65 k€', senior: '65 – 88 k€', expert: '85 – 115 k€' },
    { ville: 'Aix-en-Provence', junior: '37 – 47 k€', confirme: '47 – 63 k€', senior: '63 – 85 k€', expert: '82 – 110 k€' },
    { ville: 'Sophia Antipolis', junior: '42 – 52 k€', confirme: '52 – 70 k€', senior: '70 – 95 k€', expert: '90 – 120 k€' },
    { ville: 'Nice', junior: '40 – 50 k€', confirme: '50 – 68 k€', senior: '68 – 92 k€', expert: '88 – 118 k€' },
  ];
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3">Ville</th>
              <th className="px-4 py-3">Junior</th>
              <th className="px-4 py-3">Confirmé</th>
              <th className="px-4 py-3">Senior</th>
              <th className="px-4 py-3">Expert / Lead</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.ville} className="hover:bg-slate-50/50">
                <td className="px-4 py-3 font-semibold text-slate-900">{r.ville}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.junior}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.confirme}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.senior}</td>
                <td className="px-4 py-3 tabular-nums text-slate-700">{r.expert}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const QUESTIONS = [
  {
    q: "Tu as une nouvelle hypothèse : la version B d'une landing page convertit mieux. Comment tu la valides ?",
    good: "Définit objectif business, métrique primaire vs secondaire, puissance statistique, MDE (minimum detectable effect), durée du test, randomisation par user/session, contrôle des effets confondants. Connaît CUPED ou stratification pour réduire la variance. Sait quand un test est concluant et quand il ne l'est pas.",
    bad: "'On lance le test, on regarde dans 7 jours qui a gagné'. Pas de notion de puissance, pas de MDE, peek à mi-parcours, conclusions sur p-value < 0.05 sans correction.",
  },
  {
    q: "On te demande de faire un modèle de churn. Quelle est ta première étape ?",
    good: "Commence par cadrer le problème : qu'est-ce qu'un 'churn' (définition opérationnelle), quelle fenêtre de prédiction, quelle action déclenchée (campagne, offre commerciale). Vérifie la qualité des données, le déséquilibre de classes, le coût des erreurs. Modèle vient en 3e ou 4e position.",
    bad: "'Je prends scikit-learn et je teste RandomForest, XGBoost, LightGBM, je compare les AUC'. Aucune réflexion sur la définition, l'usage final, le coût des erreurs.",
  },
  {
    q: "Tu présentes les résultats d'un modèle à des stakeholders non-techniques. Comment tu structures la restitution ?",
    good: "Démarre par le contexte business + la décision attendue. Une slide / un dashboard simple. Métriques cadrées en valeur métier (coût économisé, % conversion gagnée), pas en F1. Limites du modèle expliquées clairement. Prévoit une discussion sur le go/no-go production.",
    bad: "Présente d'abord l'algorithme et les courbes ROC. Tunnel : 30 slides techniques. Ne sait pas répondre à 'et alors, qu'est-ce qu'on fait ?'.",
  },
  {
    q: "Donne-moi un exemple de modèle que tu as construit et qui n'est pas passé en prod. Pourquoi ?",
    good: "Cite un cas concret, et identifie clairement la cause : pas assez de gain métier, intégration trop coûteuse, problème de fairness/explicabilité, données pas dispo en réel, organisationnel. Tire des leçons.",
    bad: "'Tous mes modèles vont en prod' (peu probable). Ou 'jamais essayé, je faisais des POC'. Indique soit du déni soit pas d'expérience prod.",
  },
  {
    q: "Tu vois p-value = 0.04. Tu conclus quoi ?",
    good: "Rappelle que p-value 0.04 = sous H0, on aurait 4% de chances d'observer ce résultat ou plus extrême. NE conclut PAS automatiquement 'effet significatif et donc on déploie'. Demande la taille d'effet, l'intervalle de confiance, la puissance, le nombre de tests faits (multiple comparisons). Connaît la critique p-hacking.",
    bad: "'C'est significatif, donc on déploie B'. Pas de notion de taille d'effet, pas de notion de puissance, croit qu'on a 96% de chances que H1 soit vraie.",
  },
];
